import { useContext, useMemo, useCallback, useState } from 'react'
import cn from 'classnames/bind'
import moment from 'moment-timezone'
import { isEmpty, map, prop } from 'ramda'
import { useTranslation } from 'react-i18next'

import { Table, Cell, HeaderCell, HeaderRow } from 'app/common/components/Table'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import toFixedValue, { makeCnbyValue } from 'app/common/lib/toFixedValue'
import { useMonthDaysAsMoment } from 'app/common/hooks/useMonthDays'
import { BannerContent } from 'app/common/components-next/banner'
import { useCurrencySign } from 'app/common/components-next/currency'
import PlainMessageModal from 'app/common/ui-next/plain-message-modal'
import { PlainSideSlider, useDetailsControls } from 'app/common/ui-next/plain-side-slider'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

// import SideSlider, { useDetailsControls } from 'app/common/ui/SideSlider';
import RowClickable from './RowClickable'
import { History } from '../HistoryApi'
import ItemDetails from './ItemDetails'
import styles from './HistoryTable.module.scss';

/**
 * Таблица с финансами ребенка
 */

const cx = cn.bind(styles)
const cnbyValue = makeCnbyValue(styles.negative, styles.positive, '')
const cnbyValueSum = makeCnbyValue(styles.negative, '', '')
const mToday = moment()

type Props = {
  timezone: string
  date: string // YYYY-MM
  bannerPlaceId?: string
  blockDetails?: string
}

const HistoryTable = ({ date, timezone, bannerPlaceId, blockDetails }: Props) => {
  if (!timezone) {
    console.error("HistoryTable — не пришел обязательный параметр 'timezone'") // eslint-disable-line no-console
  }
  const {
    content: { data },
    loading,
  } = useContext(History)
  const currency = useCurrencySign('commaSpaceBefore')

  const { t } = useTranslation('finances')
  const tableHeaders = t('finances_student_table_columns', { returnObjects: true })

  const mDays = useMonthDaysAsMoment(date)
  const byDay = useMemo(() => prop('details', data) || {}, [data])

  const items = useMemo(() => {
    if (isEmpty(byDay)) return []
    return map((mDay) => {
      const id = mDay.format('YYYY-MM-DD')
      const item = byDay[id] || {}
      const isToday = mToday.isSame(mDay, 'day')
      return {
        id,
        day: mDay.format('DD.MM'),
        dayStartSum: toFixedValue(item.at_start),
        dayEndSum: !item.at_end && isToday ? '—' : toFixedValue(item.at_end),
        added: item.added ? `${toFixedValue(item.added)}` : '',
        subtracted: item.subtracted ? `−${toFixedValue(Math.abs(item.subtracted))}` : '',
      }
    }, mDays)
  }, [byDay, mDays])

  const emptyData = isEmpty(items)

  const [message, setMessage] = useState<string>('')
  const handleToggleMessage = useCallback(() => {
    if (message) {
      setMessage('')
      return
    }
    setMessage(blockDetails || '')
  }, [blockDetails, message])

  const { detailsID, detailsOpened, closeDetails, openDetails } = useDetailsControls()

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            className={cx(styles.flex, { sticky })}
            reference={ref}
            shadowBottom={shadow}
            skipPadding
          >
            <Table className={styles.table}>
              <HeaderRow hideBorderBottom>
                <HeaderCell>
                  {/* Дата */}
                  {tableHeaders[0]}
                </HeaderCell>
                <HeaderCell alignRight grow={3}>
                  {/* Баланс на начало дня,&nbsp;₽ */}
                  {tableHeaders[1]}
                  {currency}
                </HeaderCell>
                <HeaderCell alignRight grow={3}>
                  {/* Пополнение */}
                  {tableHeaders[2]}
                  {currency}
                </HeaderCell>
                <HeaderCell alignRight grow={3}>
                  {/* Списание */}
                  {tableHeaders[3]}
                  {currency}
                </HeaderCell>
                <HeaderCell alignRight grow={3}>
                  {/* Баланс на конец дня,&nbsp;₽ */}
                  {tableHeaders[4]}
                  {currency}
                </HeaderCell>
              </HeaderRow>
            </Table>

            {bannerPlaceId && (
              <BannerContent
                placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
                className={styles.bannerPlaceholder}
                displayAsPlaceholder
                height={0}
              />
            )}
          </PageTemplateContent>
        )}
      </Sticky>
      <PageTemplateContent className={cx(styles.flex, styles.layout)} skipPadding>
        <Table className={styles.table}>
          {emptyData && !loading && <EmptyWarn>{'Нет данных в выбранный период'}</EmptyWarn>}
          {loading && emptyData && <Loading />}
          {!emptyData &&
            items.map(({ id, day, dayStartSum, added, subtracted, dayEndSum }) => (
              <RowClickable
                id={id}
                key={id}
                loading={loading}
                onClick={blockDetails ? handleToggleMessage : openDetails}
              >
                <Cell className={styles.positive}>{day}</Cell>
                <Cell alignRight grow={3} className={cnbyValueSum(dayStartSum)}>
                  {dayStartSum}
                </Cell>
                <Cell alignRight grow={3} className={cnbyValue(added)}>
                  {added}
                </Cell>
                <Cell alignRight grow={3} className={cnbyValue(subtracted)}>
                  {subtracted}
                </Cell>
                <Cell alignRight grow={3} className={cnbyValueSum(dayEndSum)}>
                  {dayEndSum}
                </Cell>
              </RowClickable>
            ))}
        </Table>

        {bannerPlaceId && (
          <BannerContent
            // leaveContainerOnEmpty
            refreshOnMount
            className={styles.bannerContent}
            placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
          />
        )}
      </PageTemplateContent>

      <PlainSideSlider
        open={detailsOpened}
        onCrossClick={closeDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <ItemDetails
          timezone={timezone}
          date={detailsID || undefined}
          // @ts-ignore TODO
          details={byDay[detailsID] || []}
        />
      </PlainSideSlider>

      {/* <SideSlider
        open={detailsOpen}
        onCrossClick={handleCloseDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <ItemDetails
          timezone={timezone}
          date={((detailsId: any): string)}
          details={byDay[detailsId] || []}
        />
      </SideSlider> */}

      <PlainMessageModal
        externalState={Boolean(message)}
        onClose={handleToggleMessage}
        messageText={message}
        closeButtonTitle="OK"
      />
    </>
  )
}

export default HistoryTable
